import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Alert, Tabs, Tab } from "@mui/material";
import NewListComponent from "../../Templates/NewListComponent";
import { listCombinations } from "./api/propulsion";
import { loadComparisonSets, deleteComparisonSets, comparisonDate } from "./comparisonsStore";
import { useAuthGate } from "../../../hooks/useAuthGate";
import { ACCENT } from "./accent";
import { comboPath, compareWithPath, compareIdsPath, CATALOG_PATH, NEW_PATH } from "./paths";

const DATA_OPTIONS = [
  { value: "Measured", label: "Measured" },
  { value: "Modeled", label: "Modeled" },
];
const VERIFIED_OPTIONS = [
  { value: "Verified", label: "Verified" },
  { value: "Unverified", label: "Unverified" },
];

const PropulsionCalc = () => {
  const navigate = useNavigate();
  const { isAuthenticated, requireSignin } = useAuthGate();
  const [tab, setTab] = useState(0);
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  // Saved comparisons — account-synced when signed in, this-browser otherwise
  // (written by the calculator's Comparison tab).
  const [comparisons, setComparisons] = useState([]);
  const [cmpSource, setCmpSource] = useState("local");
  const [cmpLoading, setCmpLoading] = useState(true);
  const [cmpSearch, setCmpSearch] = useState("");
  const [selectedCmpRows, setSelectedCmpRows] = useState([]);

  useEffect(() => {
    let live = true;
    loadComparisonSets(isAuthenticated)
      .then(({ source, comparisons: comps }) => {
        if (!live) return;
        setComparisons(comps);
        setCmpSource(source);
      })
      .finally(() => live && setCmpLoading(false));
    return () => {
      live = false;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    // Saved combinations are per-user (auth-gated); publicly, show the tool with
    // a sign-in nudge rather than a 401.
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    listCombinations()
      .then(setCombos)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const open = (row) => navigate(comboPath(row.id));
  const openComparison = (row) =>
    navigate(compareWithPath(row.id));

  const compareSelected = () => {
    const ids = selectedRows.map((r) => r.id).join(",");
    navigate(compareIdsPath(ids));
  };

  const handleDeleteComparisons = (rows) => {
    const ids = (rows || []).map((r) => r.id);
    deleteComparisonSets(cmpSource, ids);
    setComparisons((s) => s.filter((c) => !ids.includes(c.id)));
    setSelectedCmpRows([]);
  };

  const actionOptions = [
    { value: "compare", label: "Compare selected", onClick: () => requireSignin(compareSelected), disabled: selectedRows.length < 2 },
    {
      value: "catalog",
      label: "Manage catalog",
      onClick: () => requireSignin(() => navigate(CATALOG_PATH)),
    },
  ];

  const columns = useMemo(
    () => [
      { id: "name", label: "Combination", type: "textfield", display: true, onClick: open },
      { id: "motor_name", label: "Motor", type: "textfield", display: true, onClick: open },
      { id: "propeller_name", label: "Propeller", type: "textfield", display: true, onClick: open },
      { id: "esc_name", label: "ESC", type: "textfield", display: true, onClick: open },
      { id: "data_label", label: "Data", type: "dropdown", display: true, options: DATA_OPTIONS, onClick: open },
      { id: "verified_label", label: "Verified", type: "dropdown", display: true, options: VERIFIED_OPTIONS, onClick: open },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const cmpColumns = useMemo(
    () => [
      { id: "name", label: "Comparison", type: "textfield", display: true, onClick: openComparison },
      { id: "setups_label", label: "Setups", type: "textfield", display: true, onClick: openComparison },
      { id: "saved_display", label: "Saved", type: "textfield", display: true, onClick: openComparison },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return combos
      .filter((c) => {
        if (!q) return true;
        return [c.name, c.motor_name, c.propeller_name, c.esc_name]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q));
      })
      .map((c) => ({
        ...c,
        combinationId: c.id,
        name: c.name || `${c.motor_name} + ${c.propeller_name}`,
        data_label: c.has_measured ? "Measured" : "Modeled",
        verified_label: c.verified ? "Verified" : "Unverified",
      }));
  }, [combos, search]);

  const cmpRows = useMemo(() => {
    const q = cmpSearch.trim().toLowerCase();
    return comparisons
      .filter((c) => !q || (c.name || "").toLowerCase().includes(q))
      .map((c) => ({
        ...c,
        comparisonId: c.id,
        setups_label: `${c.entries?.length ?? 0} setup${(c.entries?.length ?? 0) === 1 ? "" : "s"}`,
        saved_display: comparisonDate(c),
      }));
  }, [comparisons, cmpSearch]);

  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      {!isAuthenticated && (
        <Alert
          severity="info"
          sx={{ mx: 4, mt: 2 }}
          action={
            <Button
              size="small"
              onClick={() => requireSignin()}
              sx={{ color: ACCENT, textTransform: "none", fontWeight: 600 }}
            >
              Sign in
            </Button>
          }
        >
          You’re viewing the Propulsion Calculator publicly. Sign in to save combinations, manage the catalog and use measured data.
        </Alert>
      )}
      {error && (
        <Box sx={{ px: 4, pt: 2 }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      )}

      <Box sx={{ px: 4, pt: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            "& .MuiTabs-indicator": { backgroundColor: ACCENT },
            "& .Mui-selected": { color: `${ACCENT} !important` },
          }}
        >
          <Tab label={`Combinations${combos.length ? ` (${combos.length})` : ""}`} sx={{ textTransform: "none" }} />
          <Tab label={`Comparisons${comparisons.length ? ` (${comparisons.length})` : ""}`} sx={{ textTransform: "none" }} />
        </Tabs>
      </Box>

      {tab === 0 && (
        <NewListComponent
          title="Propulsion Calculator"
          subtitle="Simulate a motor + propeller + ESC + battery combination · thrust, flight time, TWR"
          columns={columns}
          data={rows}
          object="combination"
          isLoading={loading}
          addButtonText="New calculation"
          onAddClick={() => requireSignin(() => navigate(NEW_PATH))}
          setShowAddForm={() => requireSignin(() => navigate(NEW_PATH))}
          emptyText="No combinations yet"
          emptySubtext="Start a new calculation to mix and match a motor, propeller, ESC and battery."
          showActions
          actionOptions={actionOptions}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          setFormObject={() => {}}
          sortBy="name"
          sortingOrder="asc"
          limit={15}
          setData={() => {}}
          handleNext={({ searchText } = {}) => setSearch(searchText ?? "")}
        />
      )}

      {tab === 1 && (
        <NewListComponent
          title="Comparisons"
          subtitle={`Saved side-by-side setup comparisons from the calculator${cmpSource === "account" ? " · synced to your account" : " · stored in this browser"}`}
          columns={cmpColumns}
          data={cmpRows}
          object="comparison"
          isLoading={cmpLoading}
          sortBy="saved_display"
          addButtonText="New comparison"
          onAddClick={() => navigate(NEW_PATH)}
          setShowAddForm={() => navigate(NEW_PATH)}
          emptyText="No saved comparisons yet"
          emptySubtext="Open the calculator, pin a few setups and hit “Save comparison”."
          showActions
          onDeleteClick={handleDeleteComparisons}
          selectedRows={selectedCmpRows}
          setSelectedRows={setSelectedCmpRows}
          setFormObject={() => {}}
          sortingOrder="desc"
          limit={10}
          setData={() => {}}
          handleNext={({ searchText } = {}) => setCmpSearch(searchText ?? "")}
        />
      )}
    </Box>
  );
};

export default PropulsionCalc;
